/**
 * filterUtils — v2.67
 * Helpers for forwarding admin-defined custom filters from the React
 * filter state to the `/api/properties` query params.
 *
 * Why this exists:
 * --------------------------------------------------------------------
 * Dashboard.jsx / DealerExplore.jsx / TeamDashboard.jsx each build a
 * `params` object by manually copying canonical filter keys (bhk,
 * listing_type, main_area, etc.). Admin-defined custom filters
 * (parking, rent_lease, furnishing, vastu, …) are stored in the SAME
 * `filters` state object under their own keys, but were never being
 * forwarded to the backend — leaving the user with the impression that
 * "filters don't work, all properties keep showing."
 *
 * This helper iterates active custom filters (from useConfig().filters)
 * and appends their values to params with the correct shape per type:
 *   • dropdown → params[key] = value
 *   • toggle   → params[key] = "1"   (backend accepts "1"/"true"/"yes")
 *   • slider   → params[`max_${key}`] = value
 *                (only when value < f.max, so "Any" doesn't add noise)
 *
 * The function is pure (no side effects beyond mutating `params`) and
 * defensive against missing/malformed config entries.
 */

// Keys already handled explicitly in each load() — don't double-forward
// (the canonical block below would just no-op since the value lives at
// the same key, but the guard keeps intent explicit and future-proofs
// against any handler that uses a special-case key like `area`).
const CANONICAL_KEYS = new Set([
  "main_area", "sub_area", "area", "bhk", "listing_type", "property_type",
  "tag", "price", "priceRange", "metro_distance", "maxMetro",
  "best_deal", "premium_deal",
]);

export function appendCustomFilterParams(params, filters, configFilters) {
  if (!params || !filters || !Array.isArray(configFilters)) return;
  for (const f of configFilters) {
    if (!f || !f.is_custom || f.status !== "active") continue;
    const key = f.key;
    if (!key || CANONICAL_KEYS.has(key)) continue;
    const v = filters[key];
    if (v === undefined || v === null || v === "") continue;

    if (f.type === "toggle") {
      if (v) params[key] = "1";
    } else if (f.type === "dropdown") {
      params[key] = v;
    } else if (f.type === "slider") {
      // FilterPanel stores slider value as a single number (max threshold).
      // Backend expects min_<key> / max_<key>; we only set max_<key> and
      // only when the user has moved the slider below the configured max.
      const numV = Number(v);
      if (Number.isFinite(numV) && (f.max == null || numV < f.max)) {
        params[`max_${key}`] = numV;
      }
    }
  }
}
